import React, { useState } from "react";
import { Card, IndexTable, Text, LegacyStack, Checkbox, ButtonGroup, Button } from "@shopify/polaris";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";

import Pagination from "./Pagination";
import Row from "./Row";
import UpdateSteps from "./UpdateSteps";
import { usePriceChanges } from "./Context";

export default function Table(): React.JSX.Element {
  const { resources, updateMany, ignoreMany, toggleAll } = usePriceChanges();

  const { addToast } = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const pending = resources.filter(resource => resource.status === "pending");
  const pendingCount = pending.length;

  const selected = resources.filter(resource => resource.status === "pending" && resource.selected);
  const selectedCount = selected.length;

  const noneSelected = selectedCount === 0;

  const affectedSubscriptionsCount = selected.reduce(
    (sum, resource) => sum + resource.affectedSubscriptionsCount,
    0
  );

  const handleUpdate = async (notify: boolean): Promise<void> => {
    try {
      setIsModalOpen(false);

      const uuids = selected.map(resource => resource.uuid);

      await updateMany(uuids, notify);

      addToast(`
        Bulk price update queued for ${affectedSubscriptionsCount} ${affectedSubscriptionsCount === 1 ? "subscription" : "subscriptions"}
        across ${selectedCount} ${selectedCount === 1 ? "product" : "products"}. ${notify ? "Customers will be notified when complete." : ""}
      `);
    } catch {
      addToast(`Something went wrong updating some of the products. Please try again.`, true);
    }
  };

  const handleIgnore = async (): Promise<void> => {
    try {
      const uuids = selected.map(resource => resource.uuid);

      await ignoreMany(uuids);

      addToast(
        `Product price changes ignored for ${selectedCount} ${selectedCount === 1 ? "product" : "products"}.`
      );
    } catch {
      addToast(`Something went wrong ignoring some of the products. Please try again.`, true);
    }
  };

  const CheckboxLabel = () => {
    return (
      <Text variant="bodyMd" as="p">
        <Text as="span" fontWeight="semibold">
          {selectedCount} selected&nbsp;
        </Text>

        <Text as="span" color="subdued">
          of {resources.length} {resources.length === 1 ? "product" : "products"}
        </Text>
      </Text>
    );
  };

  const Empty = () => {
    return (
      <Text variant="bodyLg" as="span" color="subdued" alignment="center">
        There are no price changes pending review.
      </Text>
    );
  };

  return (
    <React.Fragment>
      <LegacyStack spacing="loose" vertical>
        <Card padding="4">
          <LegacyStack spacing="loose" alignment="center" distribution="equalSpacing">
            <Checkbox
              label={<CheckboxLabel />}
              labelHidden={false}
              checked={resources.every(resource => resource.selected)}
              disabled={pendingCount === 0}
              onChange={toggleAll}
            />

            <ButtonGroup>
              <Button primary onClick={() => setIsModalOpen(true)} size="slim" disabled={noneSelected}>
                Update selected
              </Button>

              <Button size="slim" onClick={handleIgnore} disabled={noneSelected}>
                Ignore selected
              </Button>
            </ButtonGroup>
          </LegacyStack>
        </Card>

        <Card padding="0">
          <IndexTable
            headings={[
              { title: "", id: "checkbox", flush: true },
              { title: "Product", id: "product" },
              { title: "Current price", id: "current-price" },
              { title: "", id: "arrow", flush: true },
              { title: "New price", id: "new-price" },
              {
                title: "Affected subs",
                id: "affected-subs",
                tooltipContent:
                  "The number of active and paused subscriptions that will be affected by this price change.",
              },
              { title: "Detected", id: "detected" },
              { title: "Actions", id: "actions" },
            ]}
            itemCount={resources.length}
            selectable={false}
            emptyState={<Empty />}
          >
            {resources.map((resource, index) => (
              <Row key={resource.uuid} resource={resource} position={index} />
            ))}
          </IndexTable>
          <Pagination />
        </Card>
      </LegacyStack>

      {!!isModalOpen && (
        <UpdateSteps
          resourcesCount={selectedCount}
          affectedSubscriptionsCount={affectedSubscriptionsCount}
          onConfirmUpdate={handleUpdate}
          onCancelUpdate={() => setIsModalOpen(false)}
        />
      )}
    </React.Fragment>
  );
}
